import React from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";

import {
  Grid,
  Typography,
  Paper,
  Tooltip,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableSortLabel,
  CircularProgress,
  Fade,
  withStyles,
} from "@material-ui/core";

import SectionHeader from "../../components/UI/SectionHeader";
import { getSorting, stableSort } from "../../utilities/sorting";

import { appAction, metricAction, portfolioAction } from "../../actions";

const styles = (theme) => ({
  loadingContainer: {
    minHeight: 250,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-around",
    textAlign: "center",
  },
  wrapper: {
    minWidth: 200,
  },
  metricsContainer: {
    marginBottom: 0,
    minHeight: 120,
  },
  paper: {
    padding: 20,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "space-around",
    textAlign: "center",
    height: "100%",
  },
  metricName: {
    fontSize: 30,
    fontFamily: "Roboto",
    fontWeight: 500,
    color: theme.palette.primary.main,
  },
  metricStat: {
    fontSize: 36,
    fontWeight: 500,
  },
  heading: {
    fontSize: 30,
    [theme.breakpoints.down("sm")]: {
      fontSize: 22,
    },
  },
  tableWrapper: {
    width: "100%",
    overflowX: "auto",
    marginBottom: theme.spacing(4),
    [theme.breakpoints.down("sm")]: {
      background:
        /* Shadow covers */
        "linear-gradient(to right, white 30%, rgba(255,255,255,0))," +
        "linear-gradient(to right, rgba(255,255,255,0), white 70%) 0 100%," +
        /* Shadows */
        "radial-gradient(farthest-side at 0% 50%, rgba(0,0,0,.2), rgba(0,0,0,0))," +
        "radial-gradient(farthest-side at 100% 50%, rgba(0,0,0,.2), rgba(0,0,0,0)) 0 100%",
      backgroundPosition:
        "left center, right center, left center, right center",
      backgroundRepeat: "no-repeat",
      backgroundSize: "40px 100%, 40px 100%, 14px 100%, 14px 100%",

      /* Opera doesn't support this in the shorthand */
      backgroundAttachment: "local, local, scroll, scroll",
    },
  },
  table: {
    minWidth: 250,
    "& th": {
      color: theme.palette.primary.main,
      fontSize: 14,
      padding: "4px 10px",
      fontWeight: 500,
      height: "48px",
    },
    "& td": {
      fontSize: 14,
      padding: "4px 10px",
    },
  },
});

class Dashboard extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      order: "desc",
      orderBy: "",
      columns: [
        {
          id: "name",
          numeric: false,
          label: "Scenario",
          sortable: true,
        },
        {
          id: "count",
          numeric: false,
          label: "Total onboardings",
          sortable: true,
        },
      ],
    };
  }

  componentDidMount() {
    this.props.dispatch(appAction.setAdminNav(true));
    this.props.dispatch(appAction.toggleMainNav(false));
    this.props.dispatch(metricAction.getGeneralMetrics());
    this.props.dispatch(portfolioAction.getPortfolioScenarioTotals());
  }

  handleRequestSort = (event, property) => {
    const orderBy = property;
    let order = "desc";

    if (this.state.orderBy === property && this.state.order === "desc") {
      order = "asc";
    }

    this.setState({ order, orderBy });
  };

  createSortHandler = (property) => (event) => {
    this.handleRequestSort(event, property);
  };

  render() {
    const { columns, order, orderBy } = this.state;
    const { classes, metric, portfolio } = this.props;

    if (metric.fetchingGeneralMetrics || portfolio.fetchingScenarioTotals) {
      return (
        <div className={classes.loadingContainer}>
          <CircularProgress size={50} />
        </div>
      );
    }

    return (
      <Fade in>
        <div className={classes.wrapper}>
          <SectionHeader text={"Admin Dashboard"} />
          <section>
            <Grid
              container
              className={classes.metricsContainer}
              spacing={4}
              justify="center"
              alignItems={metric.generalMetricsError ? "center" : "stretch"}
            >
              {metric.generalMetricsError ? (
                <Grid item>
                  <Typography color="error" variant="h6">
                    {metric.generalMetricsError}
                  </Typography>
                </Grid>
              ) : (
                <React.Fragment>
                  <Grid item xs={12} md={6} lg={4}>
                    <Paper className={classes.paper}>
                      <Typography variant="h4" className={classes.metricName}>
                        Advisors
                      </Typography>
                      <Typography className={classes.metricStat}>
                        {metric.userData.count}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} md={6} lg={4}>
                    <Paper className={classes.paper}>
                      <Typography variant="h4" className={classes.metricName}>
                        Portfolios
                      </Typography>
                      <Typography className={classes.metricStat}>
                        {metric.portfolioData.count}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} md={6} lg={4}>
                    <Paper className={classes.paper}>
                      <Typography variant="h4" className={classes.metricName}>
                        Portfolios Onboarded
                      </Typography>
                      <Typography className={classes.metricStat}>
                        {metric.portfolioData.onboardedCount}
                      </Typography>
                    </Paper>
                  </Grid>
                </React.Fragment>
              )}
            </Grid>
            <Grid container>
              <Grid item xs={12}>
                <Typography variant="h4" className={classes.heading}>
                  Total Onboardings by Scenario
                </Typography>
                <div className={classes.tableWrapper}>
                  <Table className={classes.table}>
                    <TableHead>
                      <TableRow>
                        {columns.map((column) => {
                          return (
                            <TableCell
                              key={column.id}
                              align={column.numeric ? "right" : "left"}
                              sortDirection={
                                orderBy === column.id ? order : false
                              }
                            >
                              {column.sortable ? (
                                <Tooltip
                                  title="Sort"
                                  placement="top"
                                  enterDelay={300}
                                >
                                  <TableSortLabel
                                    active={orderBy === column.id}
                                    direction={order}
                                    onClick={this.createSortHandler(column.id)}
                                  >
                                    {column.label}
                                  </TableSortLabel>
                                </Tooltip>
                              ) : (
                                column.label
                              )}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {portfolio.scenarioTotals.length > 0 ? (
                        stableSort(
                          portfolio.scenarioTotals,
                          getSorting(order, orderBy)
                        ).map((scenario) => (
                          <TableRow key={scenario.name} hover={true}>
                            <TableCell>{scenario.name}</TableCell>
                            <TableCell>{scenario.count}</TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell
                            colSpan={2}
                            style={{ textAlign: "center" }}
                          >
                            {portfolio.scenarioTotalsError ? (
                              <Typography color="error">
                                {portfolio.scenarioTotalsError}
                              </Typography>
                            ) : (
                              "No Data Found"
                            )}
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </Grid>
            </Grid>
          </section>
        </div>
      </Fade>
    );
  }
}
Dashboard.propTypes = {
  metric: PropTypes.object.isRequired,
  portfolio: PropTypes.object.isRequired,
};

const mapStateToProps = (state) => {
  return {
    metric: state.metric,
    portfolio: state.portfolio,
  };
};

export default withStyles(styles)(connect(mapStateToProps)(Dashboard));



// WEBPACK FOOTER //
// ./src/containers/AdminDashboard/index.jsx